import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useCreateOfferMutation } from 'api/offerEndpoints';
import OfferForm, { OfferFormHandle, OfferFormValues } from '../OfferForm/OfferForm';

import styles from './AddOfferView.module.scss';

type Props = {
    open?: boolean;
    onClose?: () => void;
}

const AddOfferView = ({
    open = true,
    onClose,
}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const currentEntityId = useGetCurrentEntityId();
    const [createOffer, { isLoading }] = useCreateOfferMutation();
    const formRef = useRef<OfferFormHandle>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        navigate('/offers');
    };

    const handleSubmit = async (data: OfferFormValues) => {
        if (!currentEntityId) return;
        setErrorMessage('');

        try {
            await createOffer({ entityId: currentEntityId, data }).unwrap();
            handleClose();
        } catch {
            setErrorMessage(intl.formatMessage(messages.submitError));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={intl.formatMessage(messages.add)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={handleClose}
                selectorPrimaryFocus="#offer-name"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: handleClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled,
                        loading: isLoading,
                        onClick: () => formRef.current?.submit(),
                    },
                ]}
            >
                {open && (
                    <div className={styles.form}>
                        {errorMessage && (
                            <div className={styles.error}>{errorMessage}</div>
                        )}
                        <OfferForm
                            ref={formRef}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            hideActions
                            onDisabledChange={setIsSubmitDisabled}
                        />
                    </div>
                )}
            </Tearsheet>
        </>
    );
};

const messages = defineMessages({
    add: {
        id: 'offers.add.title',
        defaultMessage: 'Dodaj ofertę',
    },
    description: {
        id: 'offers.add.description',
        defaultMessage: 'Wypełnij dane nowej oferty i zapisz ją dla wybranej parafii.',
    },
    submitError: {
        id: 'offers.add.submitError',
        defaultMessage: 'Nie udało się dodać oferty. Sprawdź dane i spróbuj ponownie.',
    },
});

export default AddOfferView;
